export interface ResponsibilityTemplate {
  intro: string;
  bullets: string[];
  sectionTitle: string;
  sectionBullets: string[];
  note: string;
}

const DEFAULT_TEMPLATE: ResponsibilityTemplate = {
  intro:
    'You will be responsible for performing duties related to your role and department objectives while upholding company standards.',
  bullets: [
    'Perform duties assigned related to your role and department objectives.',
    'Collaborate with team members and report progress to the reporting manager.',
    'Maintain professionalism, confidentiality, and adherence to company policies.',
    'Participate in training, meetings, and process improvements as required.',
    'Support business operations and deliver quality outcomes within deadlines.',
  ],
  sectionTitle: 'Source Code & Intellectual Property',
  sectionBullets: [
    'All source code, software, applications, scripts, databases, APIs, documentation, designs, and technical assets created, modified, or maintained during employment shall remain the exclusive property of the company.',
    'Employees shall not copy, distribute, disclose, reuse, or retain any company-owned code, intellectual property, or confidential information without written authorization.',
    'Upon termination of employment, all access credentials, source code repositories, technical documentation, and company assets must be returned or transferred as directed by management.',
  ],
  note:
    'Note: Your role and responsibilities may evolve based on business requirements and management direction.',
};

const FULLSTACK_DEVELOPER_TEMPLATE: ResponsibilityTemplate = {
  intro:
    'You will be responsible for developing, maintaining, and improving web applications, APIs, and related systems while ensuring high standards of quality, security, and performance.',
  bullets: [
    'Design, develop, test, and maintain frontend and backend applications based on business requirements.',
    'Develop and maintain scalable web applications, APIs, databases, and system integrations.',
    'Collaborate with project managers, designers, QA engineers, and other team members to deliver assigned tasks within agreed timelines.',
    'Participate in requirement analysis, technical discussions, architecture planning, and solution design.',
    'Troubleshoot, debug, and resolve application issues in a timely manner.',
    'Optimize application performance, security, and scalability to support business growth.',
    'Maintain proper documentation of development processes, technical implementations, and system changes.',
    'Participate in code reviews and follow company coding standards and development best practices.',
    'Communicate with clients, vendors, or stakeholders whenever required to understand project requirements and provide technical assistance.',
    'Protect confidential company information, customer data, business processes, and technical assets at all times.',
  ],
  sectionTitle: 'Source Code & Intellectual Property',
  sectionBullets: [
    'All source code, software, applications, scripts, databases, APIs, documentation, designs, and technical assets created, modified, or maintained during employment shall remain the exclusive property of the company.',
    'Employees shall not copy, distribute, disclose, reuse, or retain any company-owned code, intellectual property, or confidential information without written authorization.',
    'Upon termination of employment, all access credentials, source code repositories, technical documentation, and company assets must be returned or transferred as directed by management.',
  ],
  note:
    'Note: Responsibilities may evolve based on project requirements, business needs, and technological advancements.',
};

const DATA_ENTRY_TEMPLATE: ResponsibilityTemplate = {
  intro:
    'You will be responsible for managing, maintaining, and verifying operational data related to tours, activities, merchants, bookings, and customer support while ensuring accuracy, consistency, and compliance with company standards.',
  bullets: [
    'Enter, update, verify, and maintain tour, activity, merchant, and operational data within company systems and administrative platforms.',
    'Create and manage product listings, descriptions, schedules, pricing information, and related content as assigned.',
    'Verify data accuracy, completeness, and consistency before publication or submission.',
    'Coordinate with merchants, suppliers, and internal teams to collect, update, and validate required information.',
    'Handle customer inquiries and support requests through assigned communication channels when required.',
    'Maintain accurate records of merchant information, bookings, operational updates, and customer communications.',
    'Use spreadsheets, CRM systems, administrative panels, and business tools to perform assigned duties efficiently.',
    'Generate reports, summaries, and operational records as requested by management.',
    'Identify and report data discrepancies, missing information, or operational issues to the appropriate team.',
    'Protect confidential customer information, merchant data, business records, and company assets at all times.',
  ],
  sectionTitle: 'Data Protection & Confidentiality',
  sectionBullets: [
    'All customer information, merchant records, pricing data, booking information, operational documents, and business data accessed during employment shall remain the exclusive property of the company.',
    'Employees shall not copy, distribute, disclose, export, share, or retain company-owned data, records, documents, or confidential information without written authorization.',
    'Upon termination of employment, all company documents, records, login credentials, system access rights, and related assets must be returned or surrendered as directed by management.',
  ],
  note:
    'Note: Responsibilities may evolve based on operational requirements, business priorities, and organizational needs.',
};

const CONTENT_CREATOR_TEMPLATE: ResponsibilityTemplate = {
  intro:
    "You will be responsible for planning, creating, editing, and publishing engaging digital content across the company's online platforms while maintaining brand consistency and supporting marketing objectives.",
  bullets: [
    "Create, edit, and publish engaging content including videos, reels, shorts, images, and promotional materials for the company's digital platforms.",
    "Develop creative content ideas that promote the company's products, services, destinations, activities, and brand identity.",
    'Manage and maintain content schedules across social media platforms and other digital channels.',
    'Collaborate with management and other departments to plan marketing campaigns and content strategies.',
    'Write captions, descriptions, hashtags, and other content-related copy as required.',
    'Monitor content performance, audience engagement, and platform insights to improve content effectiveness.',
    'Capture, organize, and maintain digital media assets, project files, and content libraries.',
    'Ensure all content complies with company branding guidelines, quality standards, and copyright regulations.',
    'Stay updated with industry trends, social media updates, and emerging content formats.',
    "Represent the company's brand professionally through all published content and digital communications.",
  ],
  sectionTitle: 'Content Ownership & Intellectual Property',
  sectionBullets: [
    'All videos, photographs, graphics, designs, captions, marketing materials, social media content, and creative assets produced during employment shall remain the exclusive property of the company.',
    'Employees shall not publish, distribute, reuse, sell, or retain company-owned content, media files, or confidential information without written authorization.',
    'Upon termination of employment, all content files, project assets, social media credentials, and company-owned materials must be returned or transferred as directed by management.',
  ],
  note:
    'Note: Responsibilities may evolve based on marketing requirements, business priorities, campaign objectives, and organizational needs.',
};

function normalizePosition(position: string): string {
  return position.trim().toUpperCase().replace(/[\s_-]+/g, '');
}

export function getResponsibilityTemplate(position: string): ResponsibilityTemplate {
  const normalized = normalizePosition(position);

  if (normalized.includes('FULLSTACK') && normalized.includes('DEVELOPER')) {
    return FULLSTACK_DEVELOPER_TEMPLATE;
  }

  if (normalized.includes('DATAENTRY') || (normalized.includes('DATA') && normalized.includes('ENTRY'))) {
    return DATA_ENTRY_TEMPLATE;
  }

  if (normalized.includes('CONTENT') && normalized.includes('CREATOR')) {
    return CONTENT_CREATOR_TEMPLATE;
  }

  return DEFAULT_TEMPLATE;
}
